// App.tsx

import { Navigate } from 'react-router-dom';
import { resolveFrontendApiBase } from './utils/apiBase';
import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { browserPopupRedirectResolver, signInWithPopup, GoogleAuthProvider, User, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut as signOutAuth, onAuthStateChanged, sendPasswordResetEmail, sendEmailVerification, getAdditionalUserInfo } from "firebase/auth";
import { analytics, db } from "./firebaseConfig";
import { auth } from './firebaseAuth';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc } from "firebase/firestore";
import { getAuthHeaders } from "./utils/authHeaders";
import HeaderComponent from './HeaderComponent';
import SignInModalComponent from './SignInModalComponent';
import Create from './Create';
import Account from './Account';
import Pricing from './Pricing';
import Support from './Support';
import About from './About';
import BetaSignup from './BetaSignup';
import MarketingRouteRedirect from './MarketingRouteRedirect';
import MarketingLandingPage from './MarketingLandingPage';
import ManageBooks from './ManageBooks'; 
import LanguageSurveyLanding from './LanguageSurveyLanding';
import LanguageSurveyWizard from './LanguageSurveyWizard';
import BookDetailPage from './BookDetailPage';
import { UsersSharedStateProvider } from './UserContext';
import AdminDashboard from './AdminDashboard';
import DemoManageBooks from './demo/DemoManageBooks';
import DemoBookDetailPage from './demo/DemoBookDetailPage';
import CreditHistory from './CreditHistory';
import PasswordResetComponent from './PasswordResetComponent';
import { Button, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { logEvent } from "firebase/analytics";
import Checkout from './Checkout';
import CheckoutSuccess from './CheckoutSuccess';
import * as fbPixel from './utils/fbPixel';
import { isSupportApprovalDocument, isSupportApprovalPath } from './utils/supportSecurity';
import AccountBilling from './Account/AccountBilling';
import AccountSettings from './Account/AccountSettings';
import AccountReferrals from './Account/AccountReferrals';
import AccountFAQ from './Account/AccountFAQ';
import AccountSubscription from './Account/AccountSubscription';
import AccountPromoCode from './Account/AccountPromoCode';
import AccountGifts from './Account/AccountGifts';
import AccountIntegrations from './Account/AccountIntegrations';
import { AtticusConnect, AtticusQuoteApproval } from './atticus/AtticusFlow';
import SeriesManagementPage from './SeriesManagementPage';
import LoadingCard from './components/LoadingCard';
import { isTutorialSandboxModeEnabled } from './utils/tutorialMode';
import { TutorialSandboxProvider } from './tutorials/TutorialSandboxProvider';
import { ProviderCategoryDirectory, ResourcesHub } from './Resources';
import { PublishingGuidesIndex, TolinoGuidePage } from './PublishingGuides';
import { getPublishingGuideRoute, PUBLISHING_GUIDES_ROUTE, tolinoGuide } from './publishingGuides';
import {
  HUMAN_REVIEW_PROVIDERS_ROUTE,
  LEGACY_HUMAN_REVIEW_PROVIDERS_ROUTE,
  RESOURCES_ROUTE,
} from './serviceProviderDirectory';
import {
  sanitizeSignupSource,
  sanitizeSignupSourceDetail,
} from './signupAttribution';
import { API_SESSION_EXPIRED_EVENT } from './utils/installApiAuthInterceptor';
import { sharedApiAuthRecoveryState } from './utils/apiAuthRecovery';
import {
  AUTH_STAGE_TIMEOUT_MS,
  recordAuthStage,
  runAuthStage,
} from './utils/authTelemetry';
import { syncVerifiedAccountEmail } from './utils/accountEmailSync';
import BacklistCandidateFinder from './backlistCandidateFinder/BacklistCandidateFinder';
import { candidateFinderSignupFields } from './backlistCandidateFinder/analytics';
import AgentPlatformDashboard from './AgentPlatform/AgentPlatformDashboard';
import { isAgentPlatformEnabled } from './AgentPlatform/featureFlag';

const SupportCoordinatorConnect = React.lazy(() => import('./AgentPlatform/SupportCoordinatorConnect'));
const CustomerSupportRequest = React.lazy(() => import('./AgentPlatform/CustomerSupportRequest'));
const ExternalSupportConnect = React.lazy(() => import('./AgentPlatform/ExternalSupportConnect')
  .then(module => ({ default: module.ExternalSupportConnect })));
const ExternalSupportCreditApproval = React.lazy(() => import('./AgentPlatform/ExternalSupportConnect')
  .then(module => ({ default: module.ExternalSupportCreditApproval })));
const OperatorMcpConnect = React.lazy(() => import('./Account/OperatorMcpConnect')
  .then(module => ({ default: module.OperatorMcpConnect })));

export const AnalyticsTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (isSupportApprovalDocument() || isSupportApprovalPath(location.pathname)) return;
    // Firebase Analytics
    if (analytics) {
      logEvent(analytics, "page_view", { page_path: location.pathname });
    }
    
    // Facebook Pixel pageview
    fbPixel.pageview();

    const params = new URLSearchParams(location.search);
    const refParam = sanitizeReferralCode(params.get('ref'));
    if (refParam) {
      try {
        window.localStorage.setItem(REFERRAL_STORAGE_KEY, refParam);
      } catch (error) {
        // ignore storage errors
      }
    }
  }, [location]);

  return null;
};

export const AppHeader: React.FC<any> = (props) => {
  const location = useLocation();
  const headerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (location.pathname.startsWith('/demo')) {
      document.documentElement.style.setProperty('--app-header-offset', '0px');
      return;
    }

    const node = headerRef.current;
    if (!node) {
      return;
    }

    const updateOffset = () => {
      document.documentElement.style.setProperty('--app-header-offset', `${node.offsetHeight}px`);
    };

    updateOffset();

    const resizeObserver = new ResizeObserver(updateOffset);
    resizeObserver.observe(node);

    return () => {
      resizeObserver.disconnect();
    };
  }, [location.pathname]);

  if (location.pathname.startsWith('/demo')) return null;
  return (
    <div ref={headerRef}>
      <HeaderComponent {...props} />
    </div>
  );
};

export const SignupRedirect: React.FC<{ showLoginModal: () => void; isAuth: boolean }> = ({ showLoginModal, isAuth }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refParam = sanitizeReferralCode(params.get('ref'));
    if (refParam) {
      try {
        window.localStorage.setItem(REFERRAL_STORAGE_KEY, refParam);
      } catch (error) {
        // ignore storage errors
      }
    }
    if (!isAuth) {
      showLoginModal();
    }
    navigate('/pricing', { replace: true });
  }, [location.search, showLoginModal, isAuth, navigate]);

  return null;
};

const originalConsoleError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].startsWith('ResizeObserver loop')) {
    return;
  }
  originalConsoleError(...args);
};

if (typeof window !== 'undefined') {
  window.addEventListener(
    'error',
    (event) => {
      const message = typeof event?.message === 'string' ? event.message : '';
      if (message.startsWith('ResizeObserver loop')) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true
  );
}


const API_BASE = resolveFrontendApiBase();
const REFERRAL_STORAGE_KEY = 'scribeshadow_referral_code';
const KOBO_PROMO_CODE = 'KOBOAUTHOR20K';
const AGENT_PLATFORM_ENABLED = isAgentPlatformEnabled();

export const sanitizeReferralCode = (value?: string | null): string =>
  value
    ? value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 15)
    : '';

const trackSignupSource = (method: 'email' | 'google', signupSource: string): void => {
  if (!analytics) {
    return;
  }
  try {
    const candidateAttribution = candidateFinderSignupFields();
    logEvent(analytics, 'sign_up', {
      method,
      signup_source: signupSource,
      ...(candidateAttribution.signupOriginFeature
        ? { origin_feature: candidateAttribution.signupOriginFeature }
        : {}),
      ...(candidateAttribution.signupCampaignSource
        ? { campaign_source: candidateAttribution.signupCampaignSource }
        : {}),
      ...(candidateAttribution.signupCampaignMedium
        ? { campaign_medium: candidateAttribution.signupCampaignMedium }
        : {}),
      ...(candidateAttribution.signupCampaignName
        ? { campaign_name: candidateAttribution.signupCampaignName }
        : {}),
    });
  } catch (error) {
    console.warn('Failed to record signup attribution analytics:', error);
  }
};


interface FormValues {
  username: string;
  password: string;
  actionType: string;
  referralCode?: string;
  emailConsent?: boolean;
  signupSource?: string;
  signupSourceDetail?: string;
}

export const normalizeRoles = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<string>();
  const roles: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string') {
      continue;
    }
    const role = entry.trim().toLowerCase();
    if (!role || seen.has(role)) {
      continue;
    }
    seen.add(role);
    roles.push(role);
  }
  return roles;
};

const INITIAL_USER_SHARED_STATE = {
  userID: '',
  accountType: 0,
  credits: 0,
  roles: [] as string[],
  isLangTesterUser: false,
};

type UserProfileExtras = {
  signupSource?: string;
  signupSourceDetail?: string;
  captureSignupSource?: boolean;
  signupOriginFeature?: string;
  signupCampaignSource?: string;
  signupCampaignMedium?: string;
  signupCampaignName?: string;
  signupCampaignContent?: string;
};

// Only bounded attribution hints leave the browser. Account defaults are server-owned.
export const buildUserProfilePatch = (
  firebaseUser: User,
  existingData: Record<string, any> | undefined,
  exists: boolean,
  extras: UserProfileExtras = {},
): Record<string, any> => ({ ...extras });

const buildUsersSharedState = (
  userId: string,
  userData: Record<string, any> | undefined,
) => ({
  userID: userId,
  accountType: userData?.accountType ?? 0,
  credits: userData?.credits ?? 0,
  roles: normalizeRoles(userData?.roles),
  isLangTesterUser: Boolean(
    userData?.isLangTesterUser ??
    userData?.langTester ??
    userData?.langTesterUser
  ),
});

const App: React.FC = () => {
  const [isAuth, setIsAuth] = useState(false);
  const [isLoginModalVisible, setIsLoginModalVisible] = useState(false);
  const [authEntryPoint, setAuthEntryPoint] = useState<'default' | 'candidate-finder'>('default');
  const [user, setUser] = useState<User | null>(null);
  const [authResolved, setAuthResolved] = useState<boolean>(false);
  const [isResetModalVisible, setIsResetModalVisible] = useState(false);


  // One-shot per session playful peek

  const [loading, setLoading] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showVerificationWarning, setShowVerificationWarning] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<string | null>(null);
  const [usersSharedState, setUsersSharedState] = useState(INITIAL_USER_SHARED_STATE);
  const [profileReadyUserId, setProfileReadyUserId] = useState<string | null>(null);
  const profileHydrationsRef = useRef(new Map<string, Promise<{
    existed: boolean;
    existingData: Record<string, any> | undefined;
  }>>());

  const handleResetModalCancel = () => {
    setIsResetModalVisible(false);
  };

const resetUserSharedState = () => {
  setUsersSharedState(INITIAL_USER_SHARED_STATE);
};

const applyReferralCodeForUser = async (userId: string, referralCode?: string) => {
  const sanitized = sanitizeReferralCode(referralCode);
  if (!sanitized) {
    return;
  }
  try {
    const response = await fetch(`${API_BASE}/referrals/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...await getAuthHeaders() },
      body: JSON.stringify({ userID: userId, referralCode: sanitized }),
    });
    if (response.ok) {
      message.success('Referral code saved to your account. Credits unlock after your first payment.');
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem('scribeshadow_referral_code');
        } catch (storageError) {
          // ignore storage errors
        }
      }
    } else {
      let detail: string | undefined;
      try {
        const data = await response.json();
        detail = typeof data?.detail === 'string' ? data.detail : undefined;
      } catch (error) {
        detail = undefined;
      }
      if (response.status === 409 || response.status === 400) {
        message.info(detail || 'This referral code could not be applied.');
      } else {
        console.warn('Referral apply failed', response.status, detail);
      }
    }
  } catch (error) {
    console.error('Failed to apply referral code for user:', error);
  }
};

const redeemPromoCodeForUser = async (
  userId: string,
  code?: string
): Promise<{ success: boolean; amount: number; action: string } | null> => {
  const sanitized = sanitizeReferralCode(code);
  if (!sanitized) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE}/promo/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...await getAuthHeaders() },
      body: JSON.stringify({ userID: userId, code: sanitized }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const detail = typeof data?.detail === 'string' ? data.detail : 'Failed to redeem promo code';
      message.error(detail);
      return null;
    }

    if (data?.success) {
      message.success(`Promo applied! Added ${data.amount} Forever Credits.`);
    }

    return data;
  } catch (error) {
    console.error('Failed to redeem promo code for user:', error);
    message.error('Failed to redeem promo code');
    return null;
  }
};

const establishSignedInSession = useCallback((firebaseUser: User) => {
  sharedApiAuthRecoveryState.resetTerminal();
  setIsAuth(true);
  setUser(firebaseUser);
  setIsLoginModalVisible(false);
  setAuthEntryPoint('default');
  setLoading(false);
  setError(null);
  setShowVerificationWarning(!firebaseUser.emailVerified);
  setUsersSharedState(current => (
    current.userID === firebaseUser.uid
      ? current
      : buildUsersSharedState(firebaseUser.uid, undefined)
  ));
}, []);

const tutorialModeRequested = isTutorialSandboxModeEnabled();
const tutorialUser = {
  uid: 'tutorial-demo-user',
  email: 'tutorial-demo-user@scribeshadow.local',
  emailVerified: true,
} as User;
const effectiveUser = tutorialModeRequested ? tutorialUser : user;
const effectiveAuthResolved = tutorialModeRequested ? true : authResolved;
const effectiveIsAuth = tutorialModeRequested ? true : isAuth;
const effectiveUsersSharedState = tutorialModeRequested
  ? buildUsersSharedState(tutorialUser.uid, { roles: ['tutorial'] })
  : usersSharedState;

useEffect(() => {
  const handleSessionExpired = () => {
    setIsAuth(false);
    setUser(null);
    setUsersSharedState(INITIAL_USER_SHARED_STATE);
    message.warning({
      key: 'session-expired',
      duration: 8,
      content: (
        <span>
          Your session expired. Please sign in again.
          <Button type="link" size="small" onClick={() => setIsLoginModalVisible(true)}>
            Sign in
          </Button>
        </span>
      ),
    });
  };
  window.addEventListener(API_SESSION_EXPIRED_EVENT, handleSessionExpired);
  return () => window.removeEventListener(API_SESSION_EXPIRED_EVENT, handleSessionExpired);
}, []);

const hydrateSignedInUser = useCallback(async (
  firebaseUser: User,
  extras: UserProfileExtras = {},
) => {
  const hydrationKey = [
    firebaseUser.uid,
    extras.signupSource || '',
    extras.signupSourceDetail || '',
    extras.captureSignupSource ? 'capture' : '',
    extras.signupOriginFeature || '',
    extras.signupCampaignSource || '',
    extras.signupCampaignMedium || '',
    extras.signupCampaignName || '',
    extras.signupCampaignContent || '',
  ].join(':');
  const existingHydration = profileHydrationsRef.current.get(hydrationKey);
  if (existingHydration) return existingHydration;

  const hydration = runAuthStage(
    'profile_hydration',
    async () => {
      const response = await fetch(`${API_BASE}/account/bootstrap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await getAuthHeaders(firebaseUser) },
        body: JSON.stringify(extras),
      });
      if (!response.ok) throw new Error('Account setup could not finish. Please retry or contact support.');
      const bootstrap = await response.json();
      const mergedUserData = bootstrap.profile;
      if (!mergedUserData || typeof mergedUserData !== 'object') throw new Error('Invalid account response.');
      if (auth.currentUser?.uid === firebaseUser.uid) {
        setUsersSharedState(buildUsersSharedState(firebaseUser.uid, mergedUserData));
        setProfileReadyUserId(firebaseUser.uid);
      }
      try {
        await syncVerifiedAccountEmail(firebaseUser);
      } catch (emailSyncError) {
        // Profile hydration remains usable when billing synchronization is temporarily unavailable.
        console.warn('Verified account email synchronization did not complete.', emailSyncError);
      }
      if (firebaseUser.emailVerified) {
        void getAuthHeaders(firebaseUser).then(headers => fetch(`${API_BASE}/brevo/customer-opt-in`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ reconcileOnly: true }),
        })).catch(() => { /* Pending opt-in is retried on the next verified session. */ });
      }
      return {
        existed: bootstrap.existed,
        existingData: mergedUserData,
      };
    },
    { timeoutMs: AUTH_STAGE_TIMEOUT_MS.profile },
  ).finally(() => {
    profileHydrationsRef.current.delete(hydrationKey);
  });
  profileHydrationsRef.current.set(hydrationKey, hydration);
  return hydration;
}, []);

useEffect(() => {
  const initializationStartedAt = Date.now();
  let initializationCompleted = false;
  const initializationTimer = window.setTimeout(() => {
    if (initializationCompleted) return;
    recordAuthStage(
      'initialization',
      'timeout',
      Date.now() - initializationStartedAt,
      { code: 'auth/initialization-timeout' },
    );
    setAuthResolved(true);
    setModalMessage(
      'Your browser is taking longer than expected to restore your session. Close other ScribeShadow tabs and try again if sign-in does not complete.',
    );
  }, AUTH_STAGE_TIMEOUT_MS.initialization);

  const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
    if (!initializationCompleted) {
      initializationCompleted = true;
      window.clearTimeout(initializationTimer);
      recordAuthStage('initialization', 'success', Date.now() - initializationStartedAt);
    }
    setAuthResolved(true);
    if (!firebaseUser) {
      setProfileReadyUserId(null);
      setIsAuth(false);
      setUser(null);
      setShowVerificationWarning(false);
      return;
    }

    establishSignedInSession(firebaseUser);

    void hydrateSignedInUser(firebaseUser).catch((profileError) => {
      console.error('Failed to hydrate signed-in user from Firestore', profileError);
      if (auth.currentUser?.uid === firebaseUser.uid) {
        setUsersSharedState(buildUsersSharedState(firebaseUser.uid, undefined));
      }
    });
  });

  return () => {
    window.clearTimeout(initializationTimer);
    unsubscribe();
  };
}, [establishSignedInSession, hydrateSignedInUser]);



useEffect(() => {
  if (user) {
    setShowVerificationWarning(!user.emailVerified);
  }
}, [user]);

const addUserToBrevo = async (email: string, attributes?: any) => {
  try {
    const response = await fetch('https://askpoeai-nswdzfky7q-uc.a.run.app/brevo/customer-opt-in', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(auth.currentUser ? await getAuthHeaders() : {}),
      },
      body: JSON.stringify({})
    });
    
    const data = await response.json();
    
    if (data.status === 'success') {
      console.log('User added to Brevo list #5 successfully');
    } else {
      console.error('Failed to add user to Brevo:', data.message);
    }
  } catch (error) {
    console.error('Error adding user to Brevo:', error);
  }
};

const fbTestCode = new URLSearchParams(window.location.search).get('fb_test_code');

const signInWithGoogle = async (options?: {
  requireConsent?: boolean;
  referralCode?: string;
  signupSource?: string;
  signupSourceDetail?: string;
}) => {
  const referralCode = options?.referralCode;
  const signupSource = sanitizeSignupSource(options?.signupSource);
  const signupSourceDetail = sanitizeSignupSourceDetail(signupSource, options?.signupSourceDetail);
  const candidateAttribution = candidateFinderSignupFields();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  setError(null);
  setLoading(true);
  setModalMessage(null);
  try {
    const result = await runAuthStage(
      'google_sign_in',
      () => signInWithPopup(auth, provider, browserPopupRedirectResolver),
      { timeoutMs: AUTH_STAGE_TIMEOUT_MS.signIn },
    );
    const firebaseUser = result.user;
    const isNewFirebaseUser = Boolean(getAdditionalUserInfo(result)?.isNewUser);
    establishSignedInSession(firebaseUser);

    // Profile repair and signup integrations are intentionally non-blocking. Firebase Auth success
    // is sufficient to dismiss the login UI; Safari Firestore delays must not trap the user here.
    void (async () => {
      const profileResult = await hydrateSignedInUser(firebaseUser, {
        ...(signupSource ? { signupSource } : {}),
        ...(signupSourceDetail ? { signupSourceDetail } : {}),
        ...candidateAttribution,
        ...(isNewFirebaseUser ? { captureSignupSource: true } : {}),
      });
      const shouldTreatAsNewUser =
        isNewFirebaseUser || !profileResult.existed || !profileResult.existingData?.emailAddress;

      if (shouldTreatAsNewUser && firebaseUser.email) {
        await addUserToBrevo(firebaseUser.email, {
          userId: firebaseUser.uid,
          signupDate: new Date().toISOString(),
          signupMethod: 'google',
        });
        try {
          await fetch('https://askpoeai-nswdzfky7q-uc.a.run.app/api/facebook-registration-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...await getAuthHeaders() },
            body: JSON.stringify({
              email: firebaseUser.email,
              user_id: firebaseUser.uid,
              registration_method: 'google',
              client_ip_address: null,
              client_user_agent: navigator.userAgent,
              fbc: null,
              fbp: null,
              first_name: firebaseUser.displayName?.split(' ')[0] || null,
              last_name: firebaseUser.displayName?.split(' ').slice(1).join(' ') || null,
              fb_test_code: fbTestCode || undefined,
            }),
          });
        } catch (facebookError) {
          console.error('Failed to send Facebook registration event:', facebookError);
        }
      }

      if (referralCode) {
        await applyReferralCodeForUser(firebaseUser.uid, referralCode);
      }
      if (shouldTreatAsNewUser && signupSource) {
        trackSignupSource('google', signupSource);
      }
    })().catch((postSignInError) => {
      console.error('Google post-sign-in setup did not complete:', postSignInError);
    });
  } catch (signInError: any) {
    setError(getErrorMessage(signInError?.code));
    console.error('Error during sign in with Google:', signInError?.code || 'unknown');
  } finally {
    setLoading(false);
  }
};

const onLogin = async (values: { username: string, password: string }) => {
  setError(null);
  setLoading(true);
  setModalMessage(null);
  const emailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailFormat.test(values.username)) {
    setError("Please enter a valid email address.");
    setLoading(false);
    return;
  }

  try {
    const userCredential = await runAuthStage(
      'password_sign_in',
      () => signInWithEmailAndPassword(auth, values.username, values.password),
      { timeoutMs: AUTH_STAGE_TIMEOUT_MS.signIn },
    );
    establishSignedInSession(userCredential.user);
    void hydrateSignedInUser(userCredential.user).catch((profileError) => {
      console.error('Password sign-in profile hydration did not complete:', profileError);
    });
  } catch (signInError: any) {
    setError(getErrorMessage(signInError?.code));
    console.error('Error during sign in with Email/Password:', signInError?.code || 'unknown');
  } finally {
    setLoading(false);
  }
};

const onRegister = async (values: {
  username: string;
  password: string;
  referralCode?: string;
  signupSource?: string;
  signupSourceDetail?: string;
}) => {
  setError(null);
  setLoading(true);
  setModalMessage(null);
  const emailFormat = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  
  if (!emailFormat.test(values.username)) {
    setError("The email address is not in correct format.");
    setLoading(false);
    return;
  }

  const signupSource = sanitizeSignupSource(values.signupSource);
  if (!signupSource) {
    setError('Please tell us how you heard about us.');
    setLoading(false);
    return;
  }
  const signupSourceDetail = sanitizeSignupSourceDetail(signupSource, values.signupSourceDetail);
  const candidateAttribution = candidateFinderSignupFields();

  try {
    const userCredential = await runAuthStage(
      'password_registration',
      () => createUserWithEmailAndPassword(auth, values.username, values.password),
      { timeoutMs: AUTH_STAGE_TIMEOUT_MS.registration },
    );
    const firebaseUser = userCredential.user;
    establishSignedInSession(firebaseUser);

    // Account integrations and Firestore profile creation continue after Auth succeeds so a slow
    // Safari Firebase service cannot leave the registration button spinning indefinitely.
    void (async () => {
      const profileHydration = hydrateSignedInUser(firebaseUser, {
        signupSource,
        ...(signupSourceDetail ? { signupSourceDetail } : {}),
        ...candidateAttribution,
        captureSignupSource: true,
      }).catch((profileError) => {
        console.error('Registration profile hydration did not complete:', profileError);
      });

      try {
        await sendEmailVerification(firebaseUser);
        message.success('Verification email sent.');
      } catch (verificationError) {
        console.error('Failed to send verification email:', verificationError);
        message.warning('Your account was created, but the verification email could not be sent. You can resend it after signing in.');
      }

      if (firebaseUser.email) {
        await addUserToBrevo(firebaseUser.email, {
          userId: firebaseUser.uid,
          signupDate: new Date().toISOString(),
          signupMethod: 'email',
        });
      }

      // Fire Facebook registration event
      try {
        await fetch('https://askpoeai-nswdzfky7q-uc.a.run.app/api/facebook-registration-event', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
        ...(auth.currentUser ? await getAuthHeaders() : {}),
          },
          body: JSON.stringify({
            email: firebaseUser.email,
            user_id: firebaseUser.uid,
            registration_method: 'email',
            client_ip_address: null,
            client_user_agent: navigator.userAgent,
            fbc: null,
            fbp: null,
            first_name: null,
            last_name: null,
            fb_test_code: fbTestCode || undefined
          })
        });
      } catch (error) {
        console.error('Failed to send Facebook registration event:', error);
      }

      await profileHydration;

      const sanitizedReferral = sanitizeReferralCode(values.referralCode);

      if (sanitizedReferral === KOBO_PROMO_CODE) {
        await redeemPromoCodeForUser(firebaseUser.uid, sanitizedReferral);
      }

      if (sanitizedReferral) {
        await applyReferralCodeForUser(firebaseUser.uid, sanitizedReferral);
      }

      trackSignupSource('email', signupSource);
    })().catch((postRegistrationError) => {
      console.error('Post-registration setup did not complete:', postRegistrationError);
    });
  } catch (registrationError: any) {
    setError(getErrorMessage(registrationError?.code));
    console.error('Error during sign up with Email/Password:', registrationError?.code || 'unknown');
  } finally {
    setLoading(false);
  }
};

const getErrorMessage = (code: string) => {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email is already in use. Please use a different email.';
    case 'auth/invalid-email':
      return 'This email is invalid. Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This user has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'User not found. Please check your email or sign up.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/weak-password':
      return 'Your password is too weak. Please try a stronger password.';
    case 'auth/operation-timeout':
      return 'Sign-in did not finish. Close other ScribeShadow tabs, then try again.';
    default:
      return 'An error occurred. Please try again later.';
  }
};


const resendVerificationEmail = async () => {
  if (user) {
    try {
      await sendEmailVerification(user);
      message.success("Verification email sent.");
    } catch (error: any) {
      console.error("Error sending verification email:", error);
      message.error("Failed to send verification email. Please try again later.");
    }
  } else {
    setError("User is not authenticated.");
  }
};

const resetPassword = (email: string) => {
  sendPasswordResetEmail(auth, email)
    .then(() => {
      // Email sent.
      console.log("Password reset email sent.");
    })
	.catch((error: any) => {
	  // An error happened.
	  console.error("Error during password reset:", error.message);
	});
};

const showLoginModal = (entryPoint: 'default' | 'candidate-finder' = 'default') => {
  setAuthEntryPoint(entryPoint);
  setIsLoginModalVisible(true);
};

const handleLoginModalCancel = () => {
  setIsLoginModalVisible(false);
  setAuthEntryPoint('default');
};

const signOut = async () => {
  try {
    await runAuthStage(
      'session_sign_out',
      () => signOutAuth(auth),
      { timeoutMs: AUTH_STAGE_TIMEOUT_MS.signOut },
    );
  } catch (signOutError) {
    console.error('Firebase sign-out did not complete:', signOutError);
  } finally {
    setUser(null);
    setIsAuth(false);
    resetUserSharedState();
    setShowVerificationWarning(false);
  }
};

const onFormSubmit = (values: FormValues) => {
  if (values.actionType === 'login') {
    onLogin(values);
  } else if (values.actionType === 'register') {
    onRegister(values);
  }
};

//		<Route 
//		path="/proofread" 
//		element={
//			<Proofread 
//			isAuth={isAuth} 
//			usersSharedState={usersSharedState} 
//			setUsersSharedState={setUsersSharedState} // pass it here
//			showLoginModal={showLoginModal} 
//			/>
//		} 
//		/>	
 

 const renderProtectedRoute = useCallback(
   (component: React.ReactElement, redirectTo: string) => {
     if (!effectiveAuthResolved) {
       return (
         <div className="loading-card-wrapper" style={{ padding: '80px 0' }}>
           <LoadingCard label="Loading..." />
         </div>
       );
     }
     if (effectiveIsAuth) {
       return component
     }
     return (
       <Navigate
         to="/manage-books"
         replace
         state={{ redirectTo }}
       />
     )
   },
   [effectiveAuthResolved, effectiveIsAuth]
 )

 const renderAdminProtectedRoute = useCallback(
   (component: React.ReactElement, redirectTo: string) => {
     if (!authResolved) {
       return (
         <div className="loading-card-wrapper" style={{ padding: '80px 0' }}>
           <LoadingCard label="Loading..." />
         </div>
       )
     }
     const roles = normalizeRoles(usersSharedState.roles)
     if (isAuth && roles.includes('admin')) {
       return component
     }
     return <Navigate to="/manage-books" replace state={{ redirectTo }} />
   },
   [authResolved, isAuth, usersSharedState.roles]
 )

  return (
    <UsersSharedStateProvider value={{ userID: effectiveUsersSharedState.userID, roles: effectiveUsersSharedState.roles }}>
    <TutorialSandboxProvider>
    <Router>
  <AnalyticsTracker />
  {showVerificationWarning && (
    <div className="app-verification-banner" role="status" aria-live="polite">
      <ExclamationCircleOutlined className="app-verification-banner__icon" />
      <span className="app-verification-banner__copy">
        Your email is not verified. Check your inbox for the verification email.
      </span>
      <Button
        className="app-verification-banner__action"
        size="small"
        onClick={resendVerificationEmail}
      >
        Resend verification email
      </Button>
    </div>
  )}
      <AppHeader showLoginModal={showLoginModal} signOut={signOut} user={effectiveUser} usersSharedState={effectiveUsersSharedState} authResolved={effectiveAuthResolved} />
		<SignInModalComponent 
			isLoginModalVisible={isLoginModalVisible} 
			handleLoginCancel={handleLoginModalCancel} 
			onFormSubmit={onFormSubmit}
			signInWithGoogle={signInWithGoogle}
			resetPassword={resetPassword}
			error={error} // Pass the error state to the form component
			showResetModal={() => setIsResetModalVisible(true)} // Adding missing property
			setIsResetModalVisible={setIsResetModalVisible} // Adding missing property
			loading={loading} // Pass the loading state to the form component
			message={modalMessage} // Pass the message state to the form component
			checkoutPlan={checkoutPlan}
			entryPoint={authEntryPoint}
		/>
	  <PasswordResetComponent
	  isResetModalVisible={isResetModalVisible}
	  handleResetModalCancel={handleResetModalCancel}
	  resetPassword={resetPassword}
	  />
<Routes>
  <Route path="/account/integrations-connect" element={
    <React.Suspense fallback={<p role="status">Loading assistant connection…</p>}>
      <OperatorMcpConnect isAuth={isAuth} authResolved={authResolved} userId={user?.uid || ''}
        showLoginModal={showLoginModal} />
    </React.Suspense>
  } />
  <Route path="/agent-platform/support-coordinator-connect" element={
    <React.Suspense fallback={<p role="status">Loading coordinator connection…</p>}>
      <SupportCoordinatorConnect isAuth={isAuth} authResolved={authResolved} userId={user?.uid || ''}
        showLoginModal={showLoginModal} />
    </React.Suspense>
  } />
  <Route path="/support/request" element={
    <React.Suspense fallback={<p role="status">Loading support request…</p>}>
      <CustomerSupportRequest isAuth={isAuth} authResolved={authResolved} userId={user?.uid || ''}
        showLoginModal={showLoginModal} />
    </React.Suspense>
  } />
  <Route path="/agent-platform/support-connect" element={
    <React.Suspense fallback={<p role="status">Loading support connection…</p>}>
      <ExternalSupportConnect isAuth={isAuth} authResolved={authResolved} userId={user?.uid || ''}
        showLoginModal={showLoginModal} />
    </React.Suspense>
  } />
  <Route path="/agent-platform/support-credit-approval" element={
    <React.Suspense fallback={<p role="status">Loading credit approval…</p>}>
      <ExternalSupportCreditApproval isAuth={isAuth} authResolved={authResolved} userId={user?.uid || ''}
        showLoginModal={showLoginModal} />
    </React.Suspense>
  } />
  <Route path="/account/integrations/atticus/connect/:intentId" element={<AtticusConnect
    isAuth={isAuth} authResolved={authResolved} userId={user?.uid || ''}
    profileReady={Boolean(user && profileReadyUserId === user.uid)} showLoginModal={showLoginModal}
    retryProfile={async () => { if (user) await hydrateSignedInUser(user); }}
  />} />
  <Route path="/account/integrations/atticus/quotes/:quoteId" element={<AtticusQuoteApproval
    isAuth={isAuth} authResolved={authResolved} userId={user?.uid || ''}
    profileReady={Boolean(user && profileReadyUserId === user.uid)} showLoginModal={showLoginModal}
    retryProfile={async () => { if (user) await hydrateSignedInUser(user); }}
  />} />
  <Route
    path="/signup"
    element={<SignupRedirect showLoginModal={showLoginModal} isAuth={isAuth} />}
  />
  <Route
    path="/account"
    element={
      <Account
        isAuth={effectiveIsAuth}
        usersSharedState={effectiveUsersSharedState}
        setUsersSharedState={setUsersSharedState}
        showLoginModal={showLoginModal}
        authResolved={effectiveAuthResolved}
      />
    }
  >
    {/* Default to billing when someone hits /account */}
    <Route index element={<Navigate to="billing" replace />} />
    <Route path="billing" element={<AccountBilling />} />
    <Route path="subscription" element={<AccountSubscription />} />
    <Route path="settings" element={<AccountSettings />} />
    <Route path="promo" element={<AccountPromoCode />} />
    <Route path="referrals" element={<AccountReferrals />} />
    <Route path="gifts" element={<AccountGifts />} />
    <Route path="integrations" element={<AccountIntegrations />} />
    <Route path="operator-integrations" element={<AccountIntegrations operatorOnly />} />
    <Route path="faq" element={<AccountFAQ />} />
  </Route>

  {/* Keep the rest of your routes as they are */}
  <Route path="/account/overview" element={<Navigate to="/manage-books" replace />} />
  <Route path="/create" element={<Navigate to="/manage-books" replace />} />
  <Route
    path="/admin/create-book"
    element={renderAdminProtectedRoute(
      <Create isAuth={isAuth} usersSharedState={usersSharedState} />,
      '/admin/create-book',
    )}
  />
  <Route path="/pricing" element={
    <Pricing
      isAuth={effectiveIsAuth}
      usersSharedState={effectiveUsersSharedState}
      setUsersSharedState={setUsersSharedState}
      showLoginModal={showLoginModal}
    />
  } />
  <Route path="/support" element={<Support tourUserId={effectiveUsersSharedState.userID} />} />
  <Route path="/about" element={<About />} />
  <Route
    path="/translation-candidate-finder"
    element={(
      <BacklistCandidateFinder
        isAuth={isAuth}
        onCreateAccount={() => showLoginModal('candidate-finder')}
      />
    )}
  />
  <Route path="/backlist-translation-candidate-finder" element={<Navigate to="/translation-candidate-finder" replace />} />
  <Route path={RESOURCES_ROUTE} element={<ResourcesHub />} />
  <Route path={PUBLISHING_GUIDES_ROUTE} element={<PublishingGuidesIndex />} />
  <Route path={getPublishingGuideRoute(tolinoGuide)} element={<TolinoGuidePage />} />
  <Route path="/resources/providers/:categorySlug" element={<ProviderCategoryDirectory />} />
  <Route path={LEGACY_HUMAN_REVIEW_PROVIDERS_ROUTE} element={<Navigate to={HUMAN_REVIEW_PROVIDERS_ROUTE} replace />} />
  <Route path="/demo" element={<DemoManageBooks />} />
  <Route path="/demo/books/:bookguid" element={<DemoBookDetailPage />} />
  <Route path="/deepdive" element={renderProtectedRoute(<MarketingLandingPage userId={effectiveUser?.uid || ''} />, '/deepdive')} />
  <Route path="/marketing" element={<Navigate to="/deepdive" replace />} />
  <Route path="/marketing/:bookId" element={<MarketingRouteRedirect />} />
  <Route path="/betaaccess" element={
    <BetaSignup
      isAuth={effectiveIsAuth}
      usersSharedState={effectiveUsersSharedState}
      setUsersSharedState={setUsersSharedState}
      showLoginModal={showLoginModal}
    />
  } />
  <Route path="/series/manage" element={<SeriesManagementPage />} />
  <Route
    path="/agent-platform"
    element={AGENT_PLATFORM_ENABLED
      ? renderProtectedRoute(<AgentPlatformDashboard />, '/agent-platform')
      : <Navigate to="/manage-books" replace />}
  />
  <Route
    path="/manage-books"
    element={<ManageBooks user={effectiveUser} showLoginModal={showLoginModal} authResolved={effectiveAuthResolved} />}
  />
  <Route
    path="/language-survey"
    element={
      renderProtectedRoute(<LanguageSurveyLanding />, '/language-survey')
    }
  />
  <Route
    path="/language-survey/start"
    element={
      renderProtectedRoute(<LanguageSurveyWizard />, '/language-survey/start')
    }
  />
  <Route path="/book-detail/:bookId" element={<BookDetailPage />} />
  <Route path="/admindashboard" element={
    <AdminDashboard
      isAuth={effectiveIsAuth}
      usersSharedState={effectiveUsersSharedState}
      setUsersSharedState={setUsersSharedState}
      showLoginModal={showLoginModal}
      mode="admin"
    />
  } />
  <Route path="/supportdashboard" element={
    <AdminDashboard
      isAuth={isAuth}
      usersSharedState={usersSharedState}
      setUsersSharedState={setUsersSharedState}
      showLoginModal={showLoginModal}
      mode="support"
    />
  } />
  <Route path="/credit-history" element={
    <CreditHistory
      isAuth={isAuth}
      usersSharedState={usersSharedState}
      setUsersSharedState={setUsersSharedState}
      showLoginModal={showLoginModal}
    />
  } />
  <Route path="/checkout" element={
    <Checkout
      isAuth={isAuth}
      user={user}
      usersSharedState={usersSharedState}
      showLoginModal={showLoginModal}
      setCheckoutPlan={setCheckoutPlan}
    />
  } />
  <Route path="/checkout-success" element={<CheckoutSuccess onSignIn={() => setIsLoginModalVisible(true)} />} />

  {/* Send root to manage books */}
  <Route path="/" element={<Navigate to="/manage-books" replace />} />
  <Route path="*" element={<Navigate to="/manage-books" replace />} />
</Routes>



    </Router>
    </TutorialSandboxProvider>
    </UsersSharedStateProvider>
  );
};

export default App;
